/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { IState, IVisualizationRegistryEntry } from './types/StateTypes';
import CrosstabVisualizationDescriptor from '../components/visualizations/crosstab/CrosstabVisualization';
import TableVisualizationDescriptor from '../components/visualizations/table/TableVisualizationDescriptor';
import { ColumnVisualizationDescriptor } from '../components/visualizations/ColumnVisualization';
import { StackedColumnVisualizationDescriptor } from '../components/visualizations/StackedColumnVisualization';
import { PercentColumnVisualizationDescriptor } from '../components/visualizations/PercentColumnVisualization';
import { BarVisualizationDescriptor } from '../components/visualizations/BarVisualization';
import { StackedBarVisualizationDescriptor } from '../components/visualizations/StackedBarVisualization';
import { PercentBarVisualizationDescriptor } from '../components/visualizations/PercentBarVisualization';
import { SpiderColumnVisualizationDescriptor } from '../components/visualizations/SpiderColumnVisualization';
import { LineVisualizationDescriptor } from '../components/visualizations/LineVisualization';
import { SplineVisualizationDescriptor } from '../components/visualizations/SplineVisualization';
import { AreaVisualizationDescriptor } from '../components/visualizations/AreaVisualization';
import { StackedAreaVisualizationDescriptor } from '../components/visualizations/StackedAreaVisualization';
import { PercentAreaVisualizationDescriptor } from '../components/visualizations/PercentAreaVisualization';
import { AreaSplineVisualizationDescriptor } from '../components/visualizations/AreaSplineVisualization';
import { SpiderLineVisualizationDescriptor } from '../components/visualizations/SpiderLineVisualization';
import { SpiderAreaVisualizationDescriptor } from '../components/visualizations/SpiderAreaVisualization';
import { ColumnLineVisualizationDescriptor } from '../components/visualizations/ColumnLineVisualization';
import { ColumnSplineVisualizationDescriptor } from '../components/visualizations/ColumnSplineVisualization';
import { StackedColumnLineVisualizationDescriptor } from '../components/visualizations/StackedColumnLineVisualization';
import { StackedColumnSplineVisualizationDescriptor } from '../components/visualizations/StackedColumnSplineVisualization';
import { MultiAxisLineVisualizationDescriptor } from '../components/visualizations/MultiAxisLineVisualization';
import { MultiAxisSplineVisualizationDescriptor } from '../components/visualizations/MultiAxisSplineVisualization';
import { MultiAxisColumnVisualizationDescriptor } from '../components/visualizations/MultiAxisColumnVisualization';
import { TimeSeriesLineVisualizationDescriptor } from '../components/visualizations/TimeSeriesLineVisualization';
import { TimeSeriesSplineVisualizationDescriptor } from '../components/visualizations/TimeSeriesSplineVisualization';
import { TimeSeriesAreaVisualizationDescriptor } from '../components/visualizations/TimeSeriesAreaVisualization';
import { TimeSeriesAreaSplineVisualizationDescriptor } from '../components/visualizations/TimeSeriesAreaSplineVisualization';
import { ScatterVisualizationDescriptor } from '../components/visualizations/ScatterVisualization';
import { BubbleVisualizationDescriptor } from '../components/visualizations/BubbleVisualization';
import { PieVisualizationDescriptor } from '../components/visualizations/PieVisualization';
import { DualLevelPieVisualizationDescriptor } from '../components/visualizations/DualLevelPieVisualization';
import { SemiPieVisualizationDescriptor } from '../components/visualizations/SemiPieVisualization';
import { HeatMapVisualizationDescriptor } from '../components/visualizations/HeatMapVisualization';
import { TimeSeriesHeatMapVisualizationDescriptor } from '../components/visualizations/TimeSeriesHeatMapVisualization';
import { DualMeasureTreeMapVisualizationDescriptor } from '../components/visualizations/DualMeasureTreeMapVisualization';
import { TreeMapVisualizationDescriptor } from '../components/visualizations/TreeMapVisualization';
import { ParentTreeMapVisualizationDescriptor } from '../components/visualizations/ParentTreeMapVisualization';
import { GaugeVisualizationDescriptor } from '../components/visualizations/GaugeVisualization';
import { MultiLevelGaugeVisualizationDescriptor } from '../components/visualizations/MultiLevelGaugeVisualization';
import { ArcGaugeVisualizationDescriptor } from '../components/visualizations/ArcGaugeVisualization';

const typesDescriptorList = [
    // dataGrid group
    CrosstabVisualizationDescriptor,
    TableVisualizationDescriptor,

    // columnAndBar group
    ColumnVisualizationDescriptor,
    StackedColumnVisualizationDescriptor,
    PercentColumnVisualizationDescriptor,
    BarVisualizationDescriptor,
    StackedBarVisualizationDescriptor,
    PercentBarVisualizationDescriptor,
    SpiderColumnVisualizationDescriptor,

    // lineAndArea group
    LineVisualizationDescriptor,
    SplineVisualizationDescriptor,
    AreaVisualizationDescriptor,
    StackedAreaVisualizationDescriptor,
    PercentAreaVisualizationDescriptor,
    AreaSplineVisualizationDescriptor,
    SpiderLineVisualizationDescriptor,
    SpiderAreaVisualizationDescriptor,

    // dualAndMultiAxis group
    ColumnLineVisualizationDescriptor,
    ColumnSplineVisualizationDescriptor,
    StackedColumnLineVisualizationDescriptor,
    StackedColumnSplineVisualizationDescriptor,
    MultiAxisLineVisualizationDescriptor,
    MultiAxisSplineVisualizationDescriptor,
    MultiAxisColumnVisualizationDescriptor,

    // timeSeries group
    TimeSeriesLineVisualizationDescriptor,
    TimeSeriesSplineVisualizationDescriptor,
    TimeSeriesAreaVisualizationDescriptor,
    TimeSeriesAreaSplineVisualizationDescriptor,

    // scatterAndBubble group
    ScatterVisualizationDescriptor,
    BubbleVisualizationDescriptor,

    // pie group
    PieVisualizationDescriptor,
    DualLevelPieVisualizationDescriptor,
    SemiPieVisualizationDescriptor,

    // range group
    HeatMapVisualizationDescriptor,
    TimeSeriesHeatMapVisualizationDescriptor,
    DualMeasureTreeMapVisualizationDescriptor,
    TreeMapVisualizationDescriptor,
    ParentTreeMapVisualizationDescriptor,

    // gauge group
    GaugeVisualizationDescriptor,
    MultiLevelGaugeVisualizationDescriptor,
    ArcGaugeVisualizationDescriptor
];

function cloneDeep(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}

export const initializeVisualizationProperties = (state: IState): IState => (
    {
        ...state,
        visualization: typesDescriptorList.reduce((mm: any, typeDescriptor: any) => {
            const memo = mm;
            memo[typeDescriptor.name] = {
                staticProperties: {},
                dynamicProperties: typeDescriptor.defaultDynamicProperties ? cloneDeep(typeDescriptor.defaultDynamicProperties) : {}
            }

            return memo;
        }, {})
    }
)

export const resetDynamicProperties = (state: IState) => (
    {
        ...state,
        visualization: typesDescriptorList.reduce((mm: any, typeDescriptor: any) => {
            const memo = mm;
            memo[typeDescriptor.name] = {
                staticProperties: state.visualization[typeDescriptor.name],
                dynamicProperties: typeDescriptor.defaultDynamicProperties ? cloneDeep(typeDescriptor.defaultDynamicProperties) : {}
            }

            return memo;
        }, {})
    }
)

export const buildVisualizationTypesRegistry = (): IVisualizationRegistryEntry[] => (
    typesDescriptorList.map((typeDescriptor) => ({
        available: true,
        enabled: true,
        descriptor: typeDescriptor
    }))
);
